import { createSlice } from "@reduxjs/toolkit";
import {
	deleteNotification,
	deleteNotificationsAll,
	getNotificationBadgeCount,
	getNotificationCentreCountsThunk,
	getNotificationListThunk,
	markNotificationsAllRead,
	markReadNotification,
	notificationCountThunk,
} from "./notificationThunk";
import {
	iNotifcation,
	iNotificationCentreCounts,
	iNotificationFilter,
	NotificationListSurface,
} from "../../types/notificationTypes";

export type NotificationListStatus = "idle" | "loading" | "refreshing" | "loadingMore" | "loaded" | "failed";

export interface iNotificationListState {
	filter: iNotificationFilter;
	items: iNotifcation[];
	nextCursor: string | null;
	status: NotificationListStatus;
	requestId: string | null;
}

export const DEFAULT_NOTIFICATION_FILTER: iNotificationFilter = { category: null, unreadOnly: false };

const emptyList = (): iNotificationListState => ({
	filter: DEFAULT_NOTIFICATION_FILTER,
	items: [],
	nextCursor: null,
	status: "idle",
	requestId: null,
});

const isSameFilter = (a: iNotificationFilter, b: iNotificationFilter) =>
	a.category === b.category && a.unreadOnly === b.unreadOnly;

const initialState: {
	lists: Record<NotificationListSurface, iNotificationListState>;
	centreCounts: iNotificationCentreCounts | null;
	badgeCount: number;
	notificationCount: {
		aiDirectRequestNotificationCount: number;
		directRequestNotificationCount: number;
		inProgressNotificationCount: number;
		approvedNotificationCount: number;
		eventDirectoryNotificationCount: number;
		prepaidRequestNotificationCount: number;
		corporateEventRequestNotificationCount: number;
		corporateEventApprovedNotificationCount: number;
	};
} = {
	lists: {
		popover: emptyList(),
		page: emptyList(),
	},
	centreCounts: null,
	badgeCount: 0,
	notificationCount: {
		aiDirectRequestNotificationCount: 0,
		directRequestNotificationCount: 0,
		inProgressNotificationCount: 0,
		approvedNotificationCount: 0,
		eventDirectoryNotificationCount: 0,
		prepaidRequestNotificationCount: 0,
		corporateEventRequestNotificationCount: 0,
		corporateEventApprovedNotificationCount: 0,
	},
};

const notificationSlice = createSlice({
	initialState,
	name: "notifications",
	reducers: {},

	extraReducers: (builder) => {
		builder.addCase(getNotificationListThunk.pending, (state, action) => {
			const { surface, filter, cursor } = action.meta.arg;
			const list = state.lists[surface];
			if (cursor) {
				list.status = "loadingMore";
			} else if (isSameFilter(list.filter, filter) && list.items.length) {
				list.status = "refreshing";
			} else {
				list.filter = filter;
				list.items = [];
				list.nextCursor = null;
				list.status = "loading";
			}
			list.requestId = action.meta.requestId;
		});
		builder.addCase(getNotificationListThunk.fulfilled, (state, action) => {
			const list = state.lists[action.meta.arg.surface];
			if (list.requestId !== action.meta.requestId) return;
			if (action.payload.appended) {
				const loadedIds = new Set(list.items.map((notification) => notification.id));
				list.items.push(...action.payload.items.filter((notification) => !loadedIds.has(notification.id)));
			} else {
				list.items = action.payload.items;
			}
			list.filter = action.meta.arg.filter;
			list.nextCursor = action.payload.nextCursor;
			list.status = "loaded";
			list.requestId = null;
		});
		builder.addCase(getNotificationListThunk.rejected, (state, action) => {
			const list = state.lists[action.meta.arg.surface];
			if (list.requestId !== action.meta.requestId) return;
			list.status = "failed";
			list.requestId = null;
		});

		builder.addCase(getNotificationCentreCountsThunk.fulfilled, (state, action) => {
			if (action.payload) state.centreCounts = action.payload;
		});

		builder.addCase(getNotificationBadgeCount.fulfilled, (state, action) => {
			state.badgeCount = action.payload?.notificationBadgeCount ?? 0;
		});

		builder.addCase(markReadNotification.fulfilled, (state, action) => {
			Object.values(state.lists).forEach((list) =>
				list.items.forEach((notification) => {
					if (notification.id === action.meta.arg) notification.isRead = true;
				}),
			);
		});

		builder.addCase(markNotificationsAllRead.fulfilled, (state) => {
			Object.values(state.lists).forEach((list) =>
				list.items.forEach((notification) => {
					notification.isRead = true;
				}),
			);
		});

		builder.addCase(deleteNotification.fulfilled, (state, action) => {
			Object.values(state.lists).forEach((list) => {
				list.items = list.items.filter((notification) => notification.id !== action.meta.arg.id);
			});
		});

		builder.addCase(deleteNotificationsAll.fulfilled, (state) => {
			state.lists = { popover: emptyList(), page: emptyList() };
		});

		builder.addCase(notificationCountThunk.fulfilled, (state, action: any) => {
			state.notificationCount = action.payload.data;
		});
	},
});
export default notificationSlice.reducer;
